import { useCallback, useEffect, useState } from 'react'
import { ArrowDownTrayIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import WarningBanner from '@/components/WarningBanner'
import { HoverHelp } from '@/tableControls'
import { toast } from '@/store/toastStore'
import { downloadBlob } from '@/utils/downloadFile'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import {
  formatApiError,
  generateCaseSummary,
  getCaseSummary,
  downloadCaseSummaryPdf,
  isSummaryConflict,
  refreshCaseSummary,
  type SummaryMeta,
} from '@/services/projections'
import { BARE_TRIGGER } from '../gridClasses'
import { caseNameSlug } from '../caseJsonSlug'
import { useDialogFocusTrap } from '../useDialogFocusTrap'
import SummaryPreviewFrame from './SummaryPreviewFrame'
import { summaryHorizonSentence } from './summaryHorizon'

type CaseSummaryModalProps = {
  open: boolean
  caseId: number
  caseName: string
  lastClosedMonth: string
  pageCanEdit: boolean
  caseSavedAt: string | null
  onClose: () => void
}

const EDITING_DISABLED_TOOLTIP = 'Editing is disabled'
const REFRESH_SUMMARY_HELP =
  'Rebuild tables and layout from the saved case. Keeps the current wording. Does not call the model.'
const REWRITE_SUMMARY_HELP =
  'Draft this summary again. Numbers come from the saved case; only the wording changes.'

function formatGeneratedAt(iso: string): string {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day} ${h}:${min}`
}

function savedAtMatches(a: string, b: string): boolean {
  return a.slice(0, 19) === b.slice(0, 19) || new Date(a).getTime() === new Date(b).getTime()
}

function staleBannerMessage(
  caseSavedAt: string | null,
  summaryCaseSavedAt: string,
): string {
  if (caseSavedAt != null && savedAtMatches(caseSavedAt, summaryCaseSavedAt)) {
    return 'This summary was written before the latest historicals. Refresh to update numbers and layout, or Rewrite for new wording.'
  }
  return 'This summary was written before the latest save. Refresh to update numbers and layout, or Rewrite for new wording.'
}

function ActionSpinner() {
  return <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-500" aria-hidden />
}

export default function CaseSummaryModal({
  open,
  caseId,
  caseName,
  lastClosedMonth,
  pageCanEdit,
  caseSavedAt,
  onClose,
}: CaseSummaryModalProps) {
  const dialogRef = useDialogFocusTrap(open, onClose)
  const [summary, setSummary] = useState<SummaryMeta | null>(null)
  const [stale, setStale] = useState(false)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const busy = generating || refreshing

  const loadSummary = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getCaseSummary(caseId)
      setSummary(response.summary)
      setStale(response.stale)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load summary'))
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    if (!open || !caseId) {
      return
    }
    void loadSummary()
  }, [open, caseId, loadSummary])

  const handleGenerate = async (force: boolean) => {
    setGenerating(true)
    try {
      const result = await generateCaseSummary(caseId, force)
      setSummary(result)
      setStale(false)
    } catch (error) {
      toast.error(formatApiError(error, 'Summary generation failed'))
      if (isSummaryConflict(error)) {
        // formatted toast already shown
      }
    } finally {
      setGenerating(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const result = await refreshCaseSummary(caseId)
      setSummary(result)
      setStale(false)
    } catch (error) {
      toast.error(formatApiError(error, 'Summary refresh failed'))
      if (isSummaryConflict(error)) {
        // formatted toast already shown
      }
    } finally {
      setRefreshing(false)
    }
  }

  const handleDownload = async () => {
    try {
      const blob = await downloadCaseSummaryPdf(caseId)
      const filename =
        blob instanceof File && blob.name
          ? blob.name
          : `TCG-Projection-Export-${caseNameSlug(caseName)}-${
              summary?.generated_at?.slice(0, 10) ?? todayIsoDate()
            }.pdf`
      downloadBlob(filename, blob)
    } catch (error) {
      toast.error(formatApiError(error, 'PDF download failed'))
    }
  }

  if (!open) {
    return null
  }

  const horizonSentence = summaryHorizonSentence(lastClosedMonth, caseName)
  const refreshVariant = stale ? 'primary' : 'outline'
  const rewriteVariant = 'outline'
  const exportVariant = stale ? 'outline' : 'primary'

  const generateButton = (
    <Button
      variant="primary"
      size="md"
      disabled={!pageCanEdit || generating}
      onClick={() => void handleGenerate(false)}
    >
      {generating ? (
        <span className="inline-flex items-center gap-2">
          <ActionSpinner />
          Drafting…
        </span>
      ) : (
        'Generate summary'
      )}
    </Button>
  )

  const refreshButton = (
    <Button
      variant={refreshVariant}
      disabled={!pageCanEdit || busy || summary == null}
      onClick={() => void handleRefresh()}
    >
      {refreshing ? (
        <span className="inline-flex items-center gap-2">
          <ActionSpinner />
          Refreshing…
        </span>
      ) : (
        'Refresh'
      )}
    </Button>
  )

  const rewriteButton = (
    <Button
      variant={rewriteVariant}
      disabled={!pageCanEdit || busy || summary == null}
      onClick={() => void handleGenerate(true)}
    >
      {generating ? (
        <span className="inline-flex items-center gap-2">
          <ActionSpinner />
          Drafting…
        </span>
      ) : (
        'Rewrite'
      )}
    </Button>
  )

  const wrappedGenerate =
    !pageCanEdit ? (
      <HoverHelp label={generateButton} aria-label="Generate summary help">
        {EDITING_DISABLED_TOOLTIP}
      </HoverHelp>
    ) : (
      generateButton
    )

  const wrappedRefresh = (
    <span className={BARE_TRIGGER}>
      <HoverHelp label={refreshButton} aria-label="Refresh summary help">
        {pageCanEdit ? REFRESH_SUMMARY_HELP : EDITING_DISABLED_TOOLTIP}
      </HoverHelp>
    </span>
  )

  const wrappedRewrite = (
    <span className={BARE_TRIGGER}>
      <HoverHelp label={rewriteButton} aria-label="Rewrite summary help">
        {pageCanEdit ? REWRITE_SUMMARY_HELP : EDITING_DISABLED_TOOLTIP}
      </HoverHelp>
    </span>
  )

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="case-summary-title"
        className="flex max-h-[85vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h3 id="case-summary-title" className="text-base font-semibold text-gray-900">
            Case summary — {caseName}
          </h3>
          <Button variant="outline" iconOnly aria-label="Close" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : summary == null ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">{horizonSentence}</p>
              {wrappedGenerate}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {summary.prose_source === 'llm' ? (
                  <>
                    Generated {formatGeneratedAt(summary.generated_at)}
                    {summary.model ? ` · model ${summary.model}` : ''}
                  </>
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-1">
                    Generated from templates — model output failed the numbers check (
                    {summary.grounding_failures.length > 0 ? (
                      <HoverHelp
                        label={<span>{summary.grounding_failures.length} issues)</span>}
                        aria-label="Grounding failures help"
                      >
                        {summary.grounding_failures.map((failure) => (
                          <span key={failure} className="block">
                            {failure}
                          </span>
                        ))}
                      </HoverHelp>
                    ) : (
                      <span>{summary.grounding_failures.length} issues)</span>
                    )}
                  </span>
                )}
              </p>

              {stale ? (
                <WarningBanner message={staleBannerMessage(caseSavedAt, summary.case_saved_at)} />
              ) : null}

              {summary.truncated ? (
                <WarningBanner message="This summary was trimmed to fit three pages." />
              ) : null}

              <SummaryPreviewFrame html={summary.html} dimmed={busy} />
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {summary != null ? (
                <>
                  {wrappedRefresh}
                  {wrappedRewrite}
                </>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <span className={BARE_TRIGGER}>
                <HoverHelp
                  label={
                    <Button
                      variant={exportVariant}
                      size="md"
                      disabled={summary == null || busy}
                      onClick={() => void handleDownload()}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                      Export
                    </Button>
                  }
                  aria-label="Export projection report help"
                >
                  Summary pages first, then the full assumptions workbook — one PDF file
                </HoverHelp>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
